import React, { useState, useEffect } from 'react'
import { Row, ClickableIcon, Label, State, Column, EditWrapper } from './styled'
import { Input, CodeInput, Button, IconButton, SuccessMessage } from '@sabavision/dio'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { createOTP, getTokenRequest, changeIdentityAttribute } from '../../../api/profile'
import { Istate } from '../../../reducers/types'
import { IReason } from '../../../api/types'
import { updateAttribute } from '../../../actions/user'
import Validate from '../../../validations'
import validator from '../../../validations/schema/auth'
import { updateToastData } from '../../../actions/toast'

interface IeditField {
  field: 'mobile' | 'email'
  edit: boolean
}

interface Idata {
  otp: string
  mobile: string
  email: string
  step: 'getExistingOTP' | 'getExistingToken' | 'getNewOtp' | 'getNewToken' | 'change'
  new_attribute_token: string
  existing_attribute_token?: string
}

interface IgetTokenData {
  email?: string
  mobile?: string
  otp: string
  reason: IReason
}

interface IchangeAttributeRequest {
  attribute: 'email' | 'password' | 'mobile'
  value: string
  existing_attribute_token?: string
  new_attribute_token: string
}

function AccountSetting() {
  const { t } = useTranslation('translation', { keyPrefix: 'sidebar' })
  const dispatch = useDispatch()
  const user = useSelector((state: Istate) => state.user)

  const [edit, setEdit] = useState<IeditField>({
    field: 'mobile',
    edit: false
  })

  const [data, setData] = useState<Idata>({
    otp: '',
    mobile: '',
    email: '',
    step: user[edit.field]?.length ? 'getExistingOTP' : 'getNewOtp',
    new_attribute_token: ''
  })
  useEffect(() => {
    setData({ ...data, step: user[edit.field]?.length ? 'getExistingOTP' : 'getNewOtp' })
  }, [edit.field])

  const [disabledOtp, setDisabledOtp] = useState<boolean>(true)

  const updateData = (key: string, value: string) => {
    setData({ ...data, [key]: value })
  }

  const getOtpRequest = (nextStep: 'getExistingToken' | 'getNewToken') => {
    const requestData = {
      [edit.field]: data.step === 'getExistingOTP' ? user[edit.field] : data[edit.field],
      reason: `update_${edit.field}`
    }
    createOTP(
      requestData,
      () => {
        setDisabledOtp(false)
        setData({ ...data, step: nextStep })
        dispatch(
          updateToastData({
            message: 'کد با موفقیت ارسال شد.',
            type: 'success'
          })
        )
      },
      (error: any) => {
        dispatch(
          updateToastData({
            message: error.response?.data?.message,
            type: 'error'
          })
        )
        console.log(error)
      }
    )
  }

  const getToken = (
    tokenName: 'existing_attribute_token' | 'new_attribute_token',
    nextStep: 'change' | 'getNewOtp'
  ) => {
    const requestData: IgetTokenData = {
      reason: `update_${edit.field}`,
      otp: data.otp,
      [edit.field]: tokenName === 'existing_attribute_token' ? user[edit.field] : data[edit.field]
    }
    getTokenRequest(
      requestData,
      (token: any) => {
        if (nextStep === 'change') {
          changeAttributeRequest(token)
        }
        setData({ ...data, step: nextStep, [tokenName]: token, otp: '' })
        setDisabledOtp(true)
      },
      (error: any) => {
        console.log(error)
      }
    )
  }

  const changeAttributeRequest = (token: string) => {
    const requestData: IchangeAttributeRequest = {
      attribute: edit.field,
      value: data[edit.field],
      new_attribute_token: token
    }

    if (data.existing_attribute_token) {
      requestData.existing_attribute_token = data.existing_attribute_token
    }

    changeIdentityAttribute(
      user.unique_id,
      requestData,
      () => {
        setEdit({ ...edit, edit: false })
        dispatch(updateAttribute({ [edit.field]: data[edit.field] }))
        dispatch(
          updateToastData({
            message: t('toasts.' + requestData.attribute === 'mobile' ? 'update_phone' : 'update_email' + '.success', {
              phone_number: requestData.value
            }),
            type: 'success'
          })
        )
      },
      (error: any) => {
        dispatch(
          updateToastData({
            message: error.response?.data?.message,
            type: 'error'
          })
        )
        console.log(error)
      }
    )
  }

  const [error, setErrors] = useState<Record<string, string>>({})
  const validationData = (fieldName: string) => {
    let valid = true
    const constrains = validator()
    let err = {}
    const errorMessage = Validate(data, fieldName, constrains)
    err = { ...err, [fieldName]: errorMessage }
    if (errorMessage) {
      valid = false
      setErrors({ ...err })
    }
    return valid
  }

  const handleRequest = () => {
    let valid: boolean
    if (data.step === 'getExistingOTP') {
      getOtpRequest('getExistingToken')
    } else if (data.step === 'getExistingToken') {
      valid = validationData('otp')
      if (valid) {
        getToken('existing_attribute_token', 'getNewOtp')
      }
    }
    if (data.step === 'getNewOtp') {
      valid = validationData(edit.field)
      if (valid) {
        getOtpRequest('getNewToken')
      }
    } else if (data.step === 'getNewToken') {
      valid = validationData('otp')
      if (valid) {
        getToken('new_attribute_token', 'change')
      }
    }
  }

  const returnFieldsStep = () => {
    // if user used to set email or mobile
    if (data.step === 'getExistingOTP' || data.step === 'getExistingToken') {
      return <SuccessMessage message={t(`confirm_your_${edit.field}`)} />
    } else {
      return (
        <Input
          label={t(`${edit.field}_new`) + ':'}
          name={edit.field}
          error={error[edit.field] ? t(error[edit.field]) : ''}
          handleChange={(value) => {
            updateData(edit.field, value)
          }}
        />
      )
    }
  }

  if (edit.edit) {
    return (
      <EditWrapper>
        {returnFieldsStep()}
        <CodeInput
          handleChange={(value) => {
            updateData('otp', value)
          }}
          label={t('fill_otp')}
          disabled={disabledOtp}
          value={data.otp}
          error={error.otp ? t(error.otp) : null}
        />
        <Row gap="4px">
          <Button
            data-test="button-account-settings"
            label={t(data.step === 'getExistingOTP' || data.step === 'getNewOtp' ? 'confirm_and_get_otp' : 'confirm')}
            handleClick={() => {
              handleRequest()
            }}
            fullWidth
          />
          <IconButton
            icon="iris-x"
            handleClick={() => {
              setEdit({ ...edit, edit: false })
            }}
          />
        </Row>
      </EditWrapper>
    )
  }

  return (
    <Column>
      <Row className="space-between">
        <span>
          <Label className="label">{t('mobile')}:</Label>
          <State>{user.mobile ? user.mobile : t('undefined')}</State>
        </span>
        <ClickableIcon
          className="iconsax-edit-2"
          onClick={() => {
            setEdit({ field: 'mobile', edit: true })
          }}
        />
      </Row>
      <Row className="space-between">
        <span>
          <Label>{t('email')}:</Label>
          <State>{user.email ? user.email : t('undefined')}</State>
        </span>
        <ClickableIcon
          className="iconsax-edit-2"
          onClick={() => {
            setEdit({ field: 'email', edit: true })
          }}
        />
      </Row>
    </Column>
  )
}

export default AccountSetting
